'use client'

import React, { useEffect, useState } from 'react'
import { useField, FieldLabel, TextInput, useFormFields, useDocumentInfo } from '@payloadcms/ui'
import type { TextFieldClientProps } from 'payload'
import { buildSlugFromTitleSuffixYear } from '@/fields/slug/formatSlug'

export function CampSlugSuffixField({
  path,
  field,
}: Pick<TextFieldClientProps, 'path'> & { field: TextFieldClientProps['field'] }) {
  const fieldPath = path ?? field.name
  const { value, setValue } = useField<string>({ path: fieldPath })

  const title = useFormFields(([fields]) => fields.title?.value as string)
  const date = useFormFields(([fields]) => fields.date?.value as string | Date | null)
  const { id: docId } = useDocumentInfo()

  const [isDuplicate, setIsDuplicate] = useState(false)
  const [computedSlug, setComputedSlug] = useState<string | null>(null)
  const [checking, setChecking] = useState(false)

  useEffect(() => {
    const slug = buildSlugFromTitleSuffixYear(title ?? '', value ?? '', date)
    setComputedSlug(slug || null)
    if (!slug) {
      setIsDuplicate(false)
      return
    }

    const controller = new AbortController()
    setChecking(true)

    const params = new URLSearchParams()
    params.set('where[slug][equals]', slug)
    params.set('limit', '10')

    fetch(`/api/camps?${params}`, { signal: controller.signal })
      .then((res) => res.json())
      .then((data) => {
        const docs = data.docs ?? []
        const others = docId != null
          ? docs.filter((d: { id: string | number }) => String(d.id) !== String(docId))
          : docs
        setIsDuplicate(others.length > 0)
      })
      .catch(() => setIsDuplicate(false))
      .finally(() => setChecking(false))

    return () => controller.abort()
  }, [title, value, date, docId])

  return (
    <div className="field-type camp-slug-suffix-field">
      <FieldLabel htmlFor={`field-${fieldPath}`} label={field.label ?? 'Zusatz für Slug'} />
      {computedSlug && (
        <p className="mb-2 text-xs text-gray-600">
          Aktueller Slug: <strong>{computedSlug}</strong>
        </p>
      )}
      {isDuplicate && (
        <div
          className="mb-3 rounded border border-amber-500 bg-amber-50 p-3 text-sm text-amber-800"
          role="alert"
        >
          Dieser Slug ist bereits vergeben (gleicher Titel und Jahr). Bitte einen Zusatz eingeben,
          damit der Slug eindeutig wird (z. B. &quot;termin-2&quot;).
        </div>
      )}
      {checking && (
        <p className="mb-2 text-xs text-gray-500">Prüfe Slug-Eindeutigkeit…</p>
      )}
      <TextInput
        path={fieldPath}
        value={value ?? ''}
        onChange={setValue}
      />
      <p className="mt-2 text-xs text-gray-500">
        Optional. Wird im Slug vor der Jahreszahl eingefügt, z. B. wird aus
        &quot;hochkar-niederoesterreich-2025&quot; dann &quot;hochkar-niederoesterreich-ihr-text-2025&quot;.
      </p>
    </div>
  )
}
